import { Heading, Icon, Table, Tag, Text } from "@ttc3k/trekker";
import { Divider, Flex, styled } from "styled-system/jsx";
import { RoomRow } from "./RoomRow";
import { ShowAmenities } from "./ShowAmenities";
import { useBookerContext } from "widgets/booker/contexts";
import {
  formatTime,
  getAddressString,
  getMultilingualString,
  isEventProduct,
  mergeObjectsWithFallback,
} from "shared/utils";
import { useTranslation } from "react-i18next";
import { es, fr } from "date-fns/locale";
import { Bed, CoffeeCup, Map, MoneySquare } from "iconoir-react";
import { Address, EnumBookerCustomizationsAddressDisplay } from "gql/generated";
import { InfoCustom } from "shared/components/atoms/icons/InfoCustom";
import { HtmlContent } from "shared/components";
import { css } from "styled-system/css";
import { PriceDetails } from "./PriceDetails";
import { getOverriddenProductDescription } from "widgets/booker/shared/utils";

const StyledTableCaption = styled(Table.Caption, {
  base: {
    color: "text.mid!",
    fontWeight: "semiBold!",
    textTransform: "capitalize",
    fontSize: "xs!",
    lineHeight: "xs!",
    letterSpacing: "0",
  },
});

export const ProductDetails = () => {
  const { product, language, owner, booker } = useBookerContext();

  const { t } = useTranslation("booker", { keyPrefix: "PRODUCT_DETAILS" });

  // Events don't show the generic product-details block; if they have a
  // description, show just that, otherwise render nothing.
  if (isEventProduct(product)) {
    const eventDescription = getOverriddenProductDescription(
      product,
      booker,
      language,
    );
    if (!eventDescription.trim()) return null;
    return (
      <Flex
        p="250"
        bg="bg.light"
        borderRadius="md"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="border.light"
      >
        <HtmlContent
          html={eventDescription}
          className={css({ color: "text.mid" })}
        />
      </Flex>
    );
  }

  const addressObject = product?.contact?.useOwnerContact
    ? owner?.contact?.address
    : mergeObjectsWithFallback<Address>(
        (product?.contact?.address as Address) ?? {},
        owner?.contact?.address ?? {},
      );

  const addressName = product?.contact?.useOwnerContact
    ? (owner?.name ?? product.name)
    : product?.name;

  const addressDisplay =
    booker?.customizations?.addressDisplay ??
    EnumBookerCustomizationsAddressDisplay.Full;

  const addressString = !addressObject
    ? ""
    : addressDisplay === EnumBookerCustomizationsAddressDisplay.None
      ? ""
      : addressDisplay === EnumBookerCustomizationsAddressDisplay.NoStreet
        ? getAddressString({
            ...addressObject,
            line1: null,
            line2: null,
            postalCode: null,
          })
        : getAddressString(addressObject, true);
  return (
    <Flex flexDir={"column"} gap={"250"}>
      <Flex width={"full"} gap={200} alignItems={"center"}>
        <Flex gap={100} alignItems={"center"} shrink={0}>
          <InfoCustom width={"24px"} height={"24px"} />
          <Heading size={"h5"} color={"text.dark"}>
            {t("TITLE")}
          </Heading>
        </Flex>
        <Divider color={"border.light"} width={"full"} />
      </Flex>
      <Flex flexDir={"column"} gap={"300"}>
        <Table.Root>
          <StyledTableCaption>
            <Icon Element={Map} display={"inline"} />
            <styled.span paddingLeft={"100"} verticalAlign={"middle"}>
              {t("LOCATION_INFO")}
            </styled.span>
          </StyledTableCaption>
          <Table.Body>
            <Table.Row>
              <Table.Header>
                <Text visual={"smallSemiBold"} color={"text.dark"}>
                  {getMultilingualString(addressName ?? {}, language)}
                </Text>
                {addressString && (
                  <Text visual={"smallMedium"} color={"text.light"}>
                    {addressString}
                  </Text>
                )}
              </Table.Header>
            </Table.Row>
            {product?.accommodationMetadata?.checkIn && (
              <Table.Row fontSize={"xs!"} lineHeight={"xs!"}>
                <Table.Header fontWeight={"medium!"}>
                  {t("CHECK_IN")}
                </Table.Header>
                <Table.Cell>
                  {formatTime(
                    product.accommodationMetadata.checkIn,
                    language === "fr" ? fr : language === "es" ? es : undefined,
                  )}
                </Table.Cell>
              </Table.Row>
            )}
            {product?.accommodationMetadata?.checkOut && (
              <Table.Row fontSize={"xs!"} lineHeight={"xs!"}>
                <Table.Header fontWeight={"medium!"}>
                  {t("CHECK_OUT")}
                </Table.Header>
                <Table.Cell>
                  {formatTime(
                    product.accommodationMetadata.checkOut,
                    language === "fr" ? fr : language === "es" ? es : undefined,
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
        {product &&
          product.accommodationMetadata?.rooms &&
          product.accommodationMetadata.rooms.length > 0 && (
            <Table.Root>
              <StyledTableCaption borderBottom={"0 solid transparent!"}>
                <Icon Element={Bed} display={"inline"} />
                <styled.span paddingLeft={"100"} verticalAlign={"middle"}>
                  {t("BEDROOMS")}
                </styled.span>
              </StyledTableCaption>
              <Table.Body>
                {product.accommodationMetadata.rooms.map((room, i) => {
                  return (
                    <RoomRow
                      key={room?._id}
                      room={room}
                      index={i}
                      language={language}
                      translatedBedroomString={t("BEDROOM")}
                    />
                  );
                })}
              </Table.Body>
            </Table.Root>
          )}
      </Flex>
      {product &&
        product.accommodationMetadata?.amenities &&
        product.accommodationMetadata.amenities.length > 0 && (
          <Flex flexDir={"column"} gap={"250"}>
            <Text
              color={"text.mid"}
              fontWeight={"semiBold"}
              textTransform={"capitalize"}
              fontSize={"xs"}
              lineHeight={"xs"}
            >
              <Icon Element={CoffeeCup} display={"inline"} />
              <styled.span paddingLeft={"100"} verticalAlign={"middle"}>
                {t("AMENITIES")}
              </styled.span>
            </Text>
            <Flex gap={"100"} flexWrap={"wrap"}>
              {product.accommodationMetadata.amenities.map(tag => (
                <Tag mode={"light"} key={tag._id}>
                  {getMultilingualString(tag.name, language)}
                </Tag>
              ))}
              {product.accommodationMetadata.amenities.length > 10 && (
                <Tag mode={"light"}>...</Tag>
              )}
            </Flex>
          </Flex>
        )}
      <ShowAmenities />

      {/* Events drive pricing through the ticket/session selectors, so the
          generic per-product price block is hidden for them. */}
      {!isEventProduct(product) && (
        <Flex flexDir={"column"} gap={"150"}>
          <Text
            color={"text.mid"}
            fontWeight={"semiBold"}
            textTransform={"capitalize"}
            fontSize={"xs"}
            lineHeight={"xs"}
          >
            <Icon Element={MoneySquare} display={"inline"} />
            <styled.span paddingLeft={"100"} verticalAlign={"middle"}>
              {t("PRICING_DETAILS")}
            </styled.span>
          </Text>

          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            py={"200"}
            borderTopWidth={"1px"}
            borderColor={"border.light"}
            borderBottomWidth={"1px"}
          >
            <Text visual={"bodySemiBold"} color={"text.dark"}>
              {t("PRICE")}
            </Text>
            <PriceDetails pricing={product?.pricing} />
          </Flex>

          {product?.pricing?.description &&
            getMultilingualString(product.pricing.description, language)
              .length > 0 && (
              <HtmlContent
                html={getMultilingualString(
                  product.pricing.description,
                  language,
                )}
                className={css({
                  color: "text.mid",
                })}
              />
            )}
        </Flex>
      )}
    </Flex>
  );
};
